import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  orderBy,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from '../types';

const COLLECTION_NAME = 'links';

export const linkService = {
  // Create a new link
  async createLink(slug: string, destinationUrl: string, createdBy: string): Promise<string> {
    const linkData = {
      slug,
      destinationUrl,
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), linkData);
    return docRef.id;
  },

  // Get all links
  async getLinks(): Promise<Link[]> {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        slug: data.slug,
        destinationUrl: data.destinationUrl,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        createdBy: data.createdBy,
        qrCodeUrl: data.qrCodeUrl,
        hits: data.hits || 0,
        lastHitAt: data.lastHitAt?.toDate(),
        qrHits: data.qrHits || 0,
        directHits: data.directHits || 0
      };
    });
  },

  // Get link by slug
  async getLinkBySlug(slug: string): Promise<Link | null> {
    const q = query(collection(db, COLLECTION_NAME), where('slug', '==', slug));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    return {
      id: doc.id,
      slug: data.slug,
      destinationUrl: data.destinationUrl,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      createdBy: data.createdBy,
      qrCodeUrl: data.qrCodeUrl,
      hits: data.hits || 0,
      lastHitAt: data.lastHitAt?.toDate()
    };
  },

  // Track a hit on a link
  async trackHit(slug: string, isQrCode: boolean = false): Promise<void> {
    try {
      const q = query(collection(db, COLLECTION_NAME), where('slug', '==', slug));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return;
      }

      const doc = querySnapshot.docs[0];
      const data = doc.data();
      const currentHits = data.hits || 0;
      const currentQrHits = data.qrHits || 0;
      const currentDirectHits = data.directHits || 0;
      
      const updateData: any = {
        hits: currentHits + 1,
        lastHitAt: serverTimestamp()
      };
      
      if (isQrCode) {
        updateData.qrHits = currentQrHits + 1;
      } else {
        updateData.directHits = currentDirectHits + 1;
      }
      
      await updateDoc(doc.ref, updateData);
    } catch (error) {
      console.error('Error tracking hit:', error);
      throw error;
    }
  },

  // Update a link
  async updateLink(id: string, updates: Partial<Pick<Link, 'slug' | 'destinationUrl'>>): Promise<void> {
    const linkRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(linkRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  },

  // Delete a link
  async deleteLink(id: string): Promise<void> {
    const linkRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(linkRef);
  },

  // Check if slug exists
  async slugExists(slug: string, excludeId?: string): Promise<boolean> {
    const q = query(collection(db, COLLECTION_NAME), where('slug', '==', slug));
    const querySnapshot = await getDocs(q);
    
    if (excludeId) {
      return querySnapshot.docs.some(doc => doc.id !== excludeId);
    }
    
    return !querySnapshot.empty;
  }
};
